import { router } from "@inertiajs/react";
import { Download, FileCheck, Receipt, Target, FileText } from "lucide-react";
import { DetailTemplate } from "@/templates/DetailTemplate";
import { ImpactGrid, ImpactTile } from "@/portal/templates/ImpactDashboardTemplate";
import { Card } from "@/components/Card";
import { Button } from "@/components/Button";
import { StatCard } from "@/components/StatCard";
import { Table } from "@/components/Table";
import { EmptyState } from "@/components/EmptyState";

interface PayoutItem {
  index: number;
  goalName: string;
  projectName: string;
  filledAt: string | null;
  netMinor: number;
}

interface CoveredPayout {
  id: string;
  startDate: string | null;
  endDate: string | null;
  createdAt: string | null;
  amountMinor: number;
  itemsCount: number;
  viewUrl: string;
  pdfUrl: string;
}

interface StatementDoc {
  kind: "statement";
  id: string;
  label: string;
  scopeName: string;
  startDate: string | null;
  endDate: string | null;
  amountMinor: number;
  grossMinor: number;
  feeMinor: number;
  payoutCount: number;
  createdAt: string | null;
  pdfAvailable: boolean;
  pdfUrl: string;
  payouts: CoveredPayout[];
}

interface Payout {
  kind: "payout";
  id: string;
  label: string;
  startDate: string | null;
  endDate: string | null;
  createdAt: string | null;
  amountMinor: number;
  itemsCount: number;
  pdfAvailable: boolean;
  pdfUrl: string;
  items: PayoutItem[];
}

interface Props {
  statement: StatementDoc | Payout;
}

function fmtMoney(minor: number) {
  return `£${(minor / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function period(start: string | null, end: string | null) {
  if (!start && !end) return "—";
  return `${fmtDate(start)} – ${fmtDate(end)}`;
}

function payoutWhen(p: { startDate: string | null; endDate: string | null; createdAt: string | null }) {
  if (p.startDate && p.endDate) return period(p.startDate, p.endDate);
  if (p.createdAt) return `Issued ${fmtDate(p.createdAt)}`;
  return "—";
}

export default function StatementsShow({ statement: s }: Props) {
  const isPayout = s.kind === "payout";

  const downloadButton = s.pdfAvailable ? (
    <Button asChild intent="primary" size="sm">
      <a href={s.pdfUrl} target="_blank" rel="noopener noreferrer">
        <Download /> Download PDF
      </a>
    </Button>
  ) : undefined;

  return (
    <DetailTemplate
      title={isPayout ? "Payout" : s.scopeName}
      subtitle={
        s.kind === "payout" ? payoutWhen(s) : `Statement · ${period(s.startDate, s.endDate)}`
      }
      backTo="/account/statements"
      backLabel="All statements"
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Account", to: "/account" },
        { label: "Statements", to: "/account/statements" },
        { label: isPayout ? "Payout" : s.scopeName },
      ]}
      leading={
        <span className="flex size-9 shrink-0 items-center justify-center rounded-[var(--radius-md)] bg-accent-subtle text-accent-text [&_svg]:size-5">
          {isPayout ? <Receipt /> : <FileCheck />}
        </span>
      }
      meta={
        <span className="text-lg font-semibold tracking-tight text-fg tabular">{fmtMoney(s.amountMinor)}</span>
      }
      primaryAction={downloadButton}
    >
      {s.kind === "statement" ? (
        <StatementBody s={s} />
      ) : (
        <PayoutBody s={s} />
      )}
    </DetailTemplate>
  );
}

function StatementBody({ s }: { s: StatementDoc }) {
  const hasBreakdown = s.grossMinor > 0 || s.feeMinor > 0;
  const kpiCol = hasBreakdown ? 3 : 6;

  return (
    <>
      <ImpactGrid>
        <ImpactTile col={kpiCol} index={0}>
          <StatCard label="Total" value={fmtMoney(s.amountMinor)} hint="net to you" />
        </ImpactTile>
        <ImpactTile col={kpiCol} index={1}>
          <StatCard label="Payouts" value={s.payoutCount.toLocaleString()} hint="in this statement" />
        </ImpactTile>
        {hasBreakdown && (
          <>
            <ImpactTile col={3} index={2}>
              <StatCard label="Gross" value={fmtMoney(s.grossMinor)} hint="before fees" />
            </ImpactTile>
            <ImpactTile col={3} index={3}>
              <StatCard label="Fees" value={fmtMoney(s.feeMinor)} hint="platform fees" />
            </ImpactTile>
          </>
        )}

        <ImpactTile col={12} index={4}>
          <Card>
            <Card.Header
              title="Payouts covered"
              description="Payouts issued within this statement's period."
            />
            <Card.Body className={s.payouts.length === 0 ? "" : "p-0"}>
              {s.payouts.length === 0 ? (
                <EmptyState icon={<Receipt />} title="No payouts in this period" />
              ) : (
                <Table>
                  <Table.Header>
                    <tr>
                      <Table.Head className="pl-4 w-12">#</Table.Head>
                      <Table.Head>Period</Table.Head>
                      <Table.Head>Issued</Table.Head>
                      <Table.Head className="text-right">Goals</Table.Head>
                      <Table.Head className="text-right">Amount</Table.Head>
                      <Table.Head className="w-16 text-right pr-4">PDF</Table.Head>
                    </tr>
                  </Table.Header>
                  <Table.Body>
                    {s.payouts.map((p, i) => (
                      <Table.Row
                        key={p.id}
                        className="cursor-pointer"
                        onClick={() => router.visit(p.viewUrl)}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") router.visit(p.viewUrl);
                        }}
                      >
                        <Table.Cell className="pl-4 tabular text-fg-muted">{i + 1}</Table.Cell>
                        <Table.Cell className="text-fg whitespace-nowrap">
                          {period(p.startDate, p.endDate)}
                        </Table.Cell>
                        <Table.Cell className="text-fg-muted tabular whitespace-nowrap">
                          {fmtDate(p.createdAt)}
                        </Table.Cell>
                        <Table.Cell className="text-right tabular text-fg-muted">
                          {p.itemsCount.toLocaleString()}
                        </Table.Cell>
                        <Table.Cell className="text-right tabular font-medium text-fg">
                          {fmtMoney(p.amountMinor)}
                        </Table.Cell>
                        <Table.Cell className="text-right pr-4" onClick={(e) => e.stopPropagation()}>
                          <a
                            href={p.pdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Download payout PDF"
                            className="inline-flex size-7 items-center justify-center rounded-[var(--radius-sm)] text-fg-muted hover:bg-surface-hover hover:text-fg transition-colors [&_svg]:size-4"
                          >
                            <FileText />
                          </a>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              )}
            </Card.Body>
          </Card>
        </ImpactTile>
      </ImpactGrid>

      <Card className="mt-3">
        <Card.Body className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-fg-muted">
          <span>
            Scope: <span className="text-fg">{s.scopeName}</span>
          </span>
          {s.createdAt && <span className="tabular">Issued {fmtDate(s.createdAt)}</span>}
        </Card.Body>
      </Card>
    </>
  );
}

function PayoutBody({ s }: { s: Payout }) {
  return (
    <ImpactGrid>
      <ImpactTile col={6} index={0}>
        <StatCard label="Goals completed" value={s.itemsCount.toLocaleString()} hint="in this payout" />
      </ImpactTile>
      <ImpactTile col={6} index={1}>
        <StatCard label="Payout total" value={fmtMoney(s.amountMinor)} hint={payoutWhen(s)} />
      </ImpactTile>
      <ImpactTile col={12} index={2}>
        <Card>
          <Card.Header title="Payout items" description="Goals settled in this payout, with net amount." />
          <Card.Body className={s.items.length === 0 ? "" : "p-0"}>
            {s.items.length === 0 ? (
              <EmptyState icon={<Target />} title="No items" />
            ) : (
              <Table>
                <Table.Header>
                  <tr>
                    <Table.Head className="pl-4 w-12">#</Table.Head>
                    <Table.Head>Goal</Table.Head>
                    <Table.Head>Project</Table.Head>
                    <Table.Head>Filled</Table.Head>
                    <Table.Head className="text-right pr-4">Net</Table.Head>
                  </tr>
                </Table.Header>
                <Table.Body>
                  {s.items.map((item) => (
                    <Table.Row key={item.index}>
                      <Table.Cell className="pl-4 tabular text-fg-muted">{item.index}</Table.Cell>
                      <Table.Cell className="font-medium text-fg">{item.goalName}</Table.Cell>
                      <Table.Cell className="text-fg-muted">{item.projectName}</Table.Cell>
                      <Table.Cell className="text-fg-muted tabular whitespace-nowrap">
                        {fmtDate(item.filledAt)}
                      </Table.Cell>
                      <Table.Cell className="text-right pr-4 tabular font-medium text-fg">
                        {fmtMoney(item.netMinor)}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            )}
          </Card.Body>
        </Card>
      </ImpactTile>
    </ImpactGrid>
  );
}
